import React, { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "./ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "./ui/alert-dialog";
import { Save, Plus, Trash2, Pencil, Upload, ImageOff, FileSpreadsheet, Download } from "lucide-react";
import { toast } from "sonner";

const errMsg = (e) => e?.response?.data?.detail || "Une erreur est survenue";

const toPayload = (form) => ({
  code: form.code || null,
  name: form.name,
  category: form.category,
  unit: form.unit || "broche",
  price: form.price_on_request && (form.price === undefined || form.price === "") ? 0 : parseFloat(form.price),
  price_on_request: !!form.price_on_request,
  stock: parseInt(form.stock, 10) || 0,
  image_url: form.image_url || null,
  description: form.description || null,
  spec_sheet_url: form.spec_sheet_url || null,
  conditionnement: form.conditionnement || null,
  certifications: form.certifications || null,
});

const validate = (form) => {
  if (!form.name || !form.category) return "Nom et catégorie requis";
  if (!form.price_on_request && (form.price === undefined || form.price === "" || Number.isNaN(parseFloat(form.price)))) return "Prix requis";
  return null;
};

function ImageField({ value, onChange }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const res = await api.post("/uploads/product-image", fd);
      onChange(res.data.url);
      toast.success("Photo envoyée");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="col-span-2">
      <Label>Photo du produit</Label>
      <div className="mt-1 flex items-center gap-3">
        <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-md border border-border bg-muted">
          {value ? (
            <img src={value} alt="Aperçu" className="h-full w-full object-cover" data-testid="pf-image-preview" />
          ) : (
            <ImageOff className="h-6 w-6 text-muted-foreground" />
          )}
        </div>
        <div className="flex flex-1 flex-col gap-2">
          <div className="flex items-center gap-2">
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={onFile} data-testid="pf-image-file" />
            <Button type="button" size="sm" variant="outline" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="pf-image-upload-btn">
              <Upload className="mr-1.5 h-4 w-4" /> {uploading ? "Envoi…" : "Envoyer une photo"}
            </Button>
            {value && (
              <Button type="button" size="sm" variant="ghost" className="text-muted-foreground" onClick={() => onChange("")} data-testid="pf-image-clear">
                Retirer
              </Button>
            )}
          </div>
          <Input
            data-testid="pf-image"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="ou coller l'adresse d'une image (https://…)"
          />
          <p className="text-xs text-muted-foreground">JPG, PNG ou WebP, 5 Mo maximum.</p>
        </div>
      </div>
    </div>
  );
}

function ProductFields({ form, set }) {
  return (
    <div className="grid grid-cols-2 gap-3">
      <div className="col-span-2">
        <Label>Nom du produit</Label>
        <Input data-testid="pf-name" value={form.name || ""} onChange={(e) => set("name", e.target.value)} placeholder="Ex : Kebab de Poulet 25 kg" />
      </div>
      <div>
        <Label>Code article</Label>
        <Input data-testid="pf-code" value={form.code || ""} onChange={(e) => set("code", e.target.value)} placeholder="Ex : PMD15" />
      </div>
      <div>
        <Label>Catégorie</Label>
        <Input data-testid="pf-category" value={form.category || ""} onChange={(e) => set("category", e.target.value)} placeholder="Ex : Poulet" />
      </div>
      <div>
        <Label>Unité / poids</Label>
        <Input data-testid="pf-unit" value={form.unit || ""} onChange={(e) => set("unit", e.target.value)} placeholder="Ex : broche 25 kg" />
      </div>
      <div>
        <Label>Prix (€)</Label>
        <Input data-testid="pf-price" type="number" step="0.01" value={form.price ?? ""} onChange={(e) => set("price", e.target.value)} disabled={!!form.price_on_request} />
        <label className="mt-1.5 flex items-center gap-2 text-xs text-muted-foreground">
          <input type="checkbox" data-testid="pf-on-request" checked={!!form.price_on_request} onChange={(e) => set("price_on_request", e.target.checked)} />
          Prix sur demande (devis)
        </label>
      </div>
      <div>
        <Label>Stock</Label>
        <Input data-testid="pf-stock" type="number" value={form.stock ?? ""} onChange={(e) => set("stock", e.target.value)} />
      </div>
      <ImageField value={form.image_url} onChange={(v) => set("image_url", v)} />
      <div className="col-span-2">
        <Label>Fiche technique (URL PDF)</Label>
        <Input data-testid="pf-spec" value={form.spec_sheet_url || ""} onChange={(e) => set("spec_sheet_url", e.target.value)} placeholder="https://….pdf" />
      </div>
      <div className="col-span-2">
        <Label>Description</Label>
        <Input data-testid="pf-desc" value={form.description || ""} onChange={(e) => set("description", e.target.value)} />
      </div>
      <div>
        <Label>Conditionnement</Label>
        <Input data-testid="pf-conditionnement" value={form.conditionnement || ""} onChange={(e) => set("conditionnement", e.target.value)} placeholder="Ex : carton de 2 broches" />
      </div>
      <div>
        <Label>Certifications</Label>
        <Input data-testid="pf-certifications" value={form.certifications || ""} onChange={(e) => set("certifications", e.target.value)} placeholder="Ex : Halal, IFS, HACCP" />
      </div>
    </div>
  );
}

function AddProductDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ unit: "broche", stock: 0 });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    const problem = validate(form);
    if (problem) { toast.error(problem); return; }
    setBusy(true);
    try {
      await api.post("/products", toPayload(form));
      toast.success(`Produit « ${form.name} » créé`);
      setOpen(false);
      setForm({ unit: "broche", stock: 0 });
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) setForm({ unit: "broche", stock: 0 }); }}>
      <DialogTrigger asChild>
        <Button size="sm" data-testid="add-product-btn" className="gap-1.5">
          <Plus className="h-4 w-4" /> Ajouter un produit
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Nouveau produit</DialogTitle>
          <DialogDescription>Ajoutez une nouvelle broche au catalogue.</DialogDescription>
        </DialogHeader>
        <ProductFields form={form} set={set} />
        <DialogFooter>
          <Button onClick={save} disabled={busy} data-testid="save-product-btn">{busy ? "..." : "Créer le produit"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditProductDialog({ product, onDone }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(product);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    const problem = validate(form);
    if (problem) { toast.error(problem); return; }
    setBusy(true);
    try {
      await api.put(`/products/${product.id}`, toPayload(form));
      toast.success(`${form.name} mis à jour`);
      setOpen(false);
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) setForm(product); }}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid={`edit-product-${product.id}`}>
          <Pencil className="mr-1 h-4 w-4" /> Modifier
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Modifier « {product.name} »</DialogTitle>
          <DialogDescription>Nom, prix, photo, description… Les commandes déjà passées ne sont pas modifiées.</DialogDescription>
        </DialogHeader>
        <ProductFields form={form} set={set} />
        <DialogFooter>
          <Button onClick={save} disabled={busy} data-testid={`save-edit-product-${product.id}`}>{busy ? "..." : "Enregistrer"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ImportProductsDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    if (!file) { toast.error("Choisissez un fichier Excel (.xlsx)"); return; }
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post("/products/import", fd);
      setResult(data);
      toast.success(`Import terminé : ${data.created} créé(s), ${data.updated} mis à jour, ${data.photos} photo(s)`);
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) { setFile(null); setResult(null); } }}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid="import-products-btn" className="gap-1.5">
          <FileSpreadsheet className="h-4 w-4" /> Importer Excel
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Importer des produits (Excel)</DialogTitle>
          <DialogDescription>
            Téléchargez d'abord le fichier « Excel du catalogue » : il contient vos produits actuels avec leurs photos et un mode d'emploi.
            Modifiez-le ou ajoutez des lignes (photo collée sur la ligne du produit), puis chargez-le ici.
          </DialogDescription>
        </DialogHeader>
        <div>
          <Label>Fichier Excel (.xlsx)</Label>
          <Input data-testid="import-products-file" type="file" accept=".xlsx" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          <p className="mt-1 text-xs text-muted-foreground">500 produits et 15 Mo maximum. Un produit existant (même code ou même nom) est mis à jour.</p>
        </div>
        {result && (
          <div className="rounded-md border border-border bg-muted/40 p-3 text-sm" data-testid="import-products-result">
            <p className="font-semibold">{result.created} créé(s) · {result.updated} mis à jour · {result.photos} photo(s) importée(s)</p>
            {result.errors.length > 0 && (
              <ul className="mt-2 list-disc space-y-0.5 pl-5 text-xs text-destructive">
                {result.errors.map((er, i) => <li key={i}>Ligne {er.row} : {er.message}</li>)}
              </ul>
            )}
          </div>
        )}
        <DialogFooter>
          <Button onClick={submit} disabled={busy || !file} data-testid="import-products-submit">{busy ? "Import en cours…" : "Importer"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export const ProductManager = () => {
  const [products, setProducts] = useState([]);
  const [edits, setEdits] = useState({}); // id -> { price?, stock? }

  const load = () => api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const setField = (id, key, value) => setEdits((e) => ({ ...e, [id]: { ...e[id], [key]: value } }));
  const isDirty = (id) => edits[id] && Object.keys(edits[id]).length > 0;

  const save = async (p) => {
    const e = edits[p.id] || {};
    const price = e.price !== undefined && e.price !== "" ? parseFloat(e.price) : p.price;
    const stock = e.stock !== undefined && e.stock !== "" ? parseInt(e.stock, 10) : p.stock;
    try {
      await api.put(`/products/${p.id}`, {
        code: p.code,
        name: p.name,
        category: p.category,
        unit: p.unit,
        price: Number.isNaN(price) ? p.price : price,
        stock: Number.isNaN(stock) ? p.stock : stock,
        image_url: p.image_url,
        description: p.description,
        spec_sheet_url: p.spec_sheet_url,
        conditionnement: p.conditionnement,
        certifications: p.certifications,
        price_on_request: !!p.price_on_request,
      });
    } catch (err) {
      toast.error(errMsg(err));
      return;
    }
    toast.success(`${p.name} mis à jour`);
    setEdits((prev) => {
      const { [p.id]: _, ...rest } = prev;
      return rest;
    });
    load();
  };

  const downloadCatalogue = async () => {
    try {
      const res = await api.get("/products/export.xlsx", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "catalogue-produits.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Impossible de générer le fichier Excel");
    }
  };

  const remove = async (p) => {
    try {
      await api.delete(`/products/${p.id}`);
    } catch (err) {
      toast.error(errMsg(err));
      return;
    }
    toast.success(`${p.name} supprimé`);
    load();
  };

  return (
    <Card className="mt-6 overflow-hidden" data-testid="product-manager">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border p-4">
        <div>
          <h3 className="font-display font-bold">Produits, prix &amp; stock</h3>
          <p className="text-xs text-muted-foreground">Modifiez le prix et le stock directement, ou cliquez sur « Modifier » pour changer le nom, la photo ou la description. Vous pouvez aussi ajouter ou supprimer un produit.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" variant="outline" onClick={downloadCatalogue} data-testid="export-products-btn" className="gap-1.5">
            <Download className="h-4 w-4" /> Excel du catalogue
          </Button>
          <ImportProductsDialog onDone={load} />
          <AddProductDialog onDone={load} />
        </div>
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-16">Photo</TableHead>
              <TableHead>Produit</TableHead>
              <TableHead>Catégorie</TableHead>
              <TableHead className="w-40">Prix (€)</TableHead>
              <TableHead className="w-32">Stock</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {products.map((p) => (
              <TableRow key={`${p.id}-${p.price}-${p.stock}`} data-testid={`product-manage-row-${p.id}`}>
                <TableCell>
                  <div className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-md border border-border bg-muted">
                    {p.image_url ? (
                      <img src={p.image_url} alt="" className="h-full w-full object-cover" />
                    ) : (
                      <ImageOff className="h-4 w-4 text-muted-foreground" />
                    )}
                  </div>
                </TableCell>
                <TableCell className="font-medium">
                  {p.name}
                  {p.code && <div className="text-xs font-normal text-muted-foreground">{p.code}</div>}
                </TableCell>
                <TableCell><Badge variant="outline">{p.category}</Badge></TableCell>
                <TableCell>
                  <Input
                    type="number"
                    step="0.01"
                    data-testid={`price-input-${p.id}`}
                    className="h-9 w-32 tabular"
                    defaultValue={p.price}
                    onChange={(e) => setField(p.id, "price", e.target.value)}
                  />
                </TableCell>
                <TableCell>
                  <Input
                    type="number"
                    data-testid={`stock-input-${p.id}`}
                    className="h-9 w-24 tabular"
                    defaultValue={p.stock}
                    onChange={(e) => setField(p.id, "stock", e.target.value)}
                  />
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-2">
                    <Button size="sm" disabled={!isDirty(p.id)} onClick={() => save(p)} data-testid={`save-price-${p.id}`}>
                      <Save className="mr-1 h-4 w-4" /> Enregistrer
                    </Button>
                    <EditProductDialog product={p} onDone={load} />
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="sm" variant="outline" className="text-destructive" data-testid={`delete-product-${p.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Supprimer « {p.name} » ?</AlertDialogTitle>
                          <AlertDialogDescription>Cette action est définitive. Le produit sera retiré du catalogue. Les commandes déjà passées restent intactes.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel data-testid="cancel-delete-product">Annuler</AlertDialogCancel>
                          <AlertDialogAction onClick={() => remove(p)} data-testid={`confirm-delete-product-${p.id}`}>Supprimer</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {products.length === 0 && (
              <TableRow><TableCell colSpan={6} className="py-8 text-center text-muted-foreground">Aucun produit.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
};
